import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { RECEIPT_COLORS } from "@/shared/utils/paymentReceipt/receiptBrand";

import { drawPayinPdfLogo, preparePayinPdfFonts } from "./payinPdfBrand";

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

interface BrazilPayinInput {
  id: string;
  status: string;
  amountBRL: number;
  amountReceived?: number;
  copiaECola?: string;
  payloadB64?: string;
  createdAt: string;
  updatedAt?: string;
  commission?: {
    percentage: string;
    fee_fix: string;
    total_fee: number;
  };
}

const formatCurrencyBRL = (amount: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(amount);

const formatDate = (date?: string) => {
  if (!date) return "Não informado";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleString("es-CO", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Función para normalizar y formatear el estado (primera letra mayúscula, resto minúsculas)
const formatStatus = (status: string): string => {
  if (!status) return "";
  const normalized = status.toLowerCase().trim();
  if (normalized.length === 0) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

// payload_b64 ya es una imagen PNG en base64, solo necesitamos agregar el prefijo si no lo tiene
const getQrImageDataUrl = (payloadB64?: string): string | null => {
  if (!payloadB64) return null;
  // Si ya tiene el prefijo, devolverlo tal cual
  if (payloadB64.startsWith("data:image/png;base64,")) {
    return payloadB64;
  }
  // Si no tiene el prefijo, agregarlo
  return `data:image/png;base64,${payloadB64}`;
};

export const generateBrazilPayinPdf = async (payin: BrazilPayinInput) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const containerWidth = pageWidth * 0.72;
  const containerX = (pageWidth - containerWidth) / 2;
  const innerPadding = 24;
  const innerX = containerX + innerPadding;
  const contentWidth = containerWidth - innerPadding * 2;
  const startY = 96;
  let cursorY = startY;

  // The Coloca 2026 document palette, shared with the payout comprobante and with the backend's
  // transactional emails. The slate values that used to sit here (`#0f172a`, `#475569`, `#f8fafc`,
  // `#e2e8f0`, `#f1f5f9`, `#cbd5e1`) belonged to no Coloca surface at all.
  const brandColor = RECEIPT_COLORS.ink;
  const mutedColor = RECEIPT_COLORS.muted;
  const fonts = await preparePayinPdfFonts(doc);

  const statusLabel = formatStatus(payin.status || "Pending");
  const qrImageDataUrl = getQrImageDataUrl(payin.payloadB64);

  // Logo — the current wordmark, rasterized from the SVG the app renders, at its real aspect ratio.
  const logoWidth = 176;
  await drawPayinPdfLogo(doc, containerX + containerWidth - logoWidth - 12, cursorY - 24, logoWidth);

  // Encabezado
  doc.setFont(fonts.sans, "bold");
  doc.setFontSize(20);
  doc.setTextColor(brandColor);
  doc.text("Comprobante", innerX, cursorY);
  cursorY += 36;

  // Resumen de pago (QR a la izquierda, monto a la derecha)
  const summaryHeight = qrImageDataUrl ? 180 : 78;
  doc.setFillColor(RECEIPT_COLORS.surface);
  doc.roundedRect(innerX, cursorY, contentWidth, summaryHeight, 10, 10, "F");

  const leftX = innerX + 16;
  const rightX = innerX + contentWidth / 2 + 12;
  let placeNoteBelowAmount = false;

  if (qrImageDataUrl) {
    doc.setFont(fonts.sans, "bold");
    doc.setFontSize(12);
    doc.setTextColor(brandColor);
    doc.text("Código QR de pago", leftX, cursorY + 18);

    // payload_b64 ya es una imagen PNG en base64, usar directamente
    const qrTop = cursorY + 22;
    doc.addImage(qrImageDataUrl, "PNG", leftX, qrTop, 144, 144);
    placeNoteBelowAmount = true;
  }

  // Monto a pagar
  doc.setFont(fonts.sans, "normal");
  doc.setFontSize(11);
  doc.setTextColor(mutedColor);
  doc.text("Valor a pagar (BRL)", rightX, cursorY + 18);
  doc.setFont(fonts.mono, "bold");
  doc.setFontSize(22);
  doc.setTextColor(brandColor);
  const valueY = cursorY + 40;
  doc.text(formatCurrencyBRL(payin.amountBRL), rightX, valueY);

  let noteStartY = valueY + 20;
  if (typeof payin.amountReceived === "number") {
    doc.setFont(fonts.sans, "normal");
    doc.setFontSize(11);
    doc.setTextColor(mutedColor);
    const receiveLabelY = valueY + 24;
    doc.text("Monto a recibir", rightX, receiveLabelY);
    doc.setFont(fonts.mono, "normal");
    doc.setFontSize(14);
    doc.setTextColor(brandColor);
    const receiveValueY = receiveLabelY + 18;
    doc.text(formatCurrencyBRL(payin.amountReceived), rightX, receiveValueY);
    noteStartY = receiveValueY + 14;
  }

  if (placeNoteBelowAmount) {
    doc.setFont(fonts.sans, "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(mutedColor);
    const note = doc.splitTextToSize(
      "Escanéalo con tu app bancaria para completar el pago PIX.",
      contentWidth / 2 - 32,
    );
    doc.text(note, rightX, noteStartY);
  }

  cursorY += summaryHeight + 20;

  // Detalle principal
  autoTable(doc, {
    startY: cursorY,
    head: [["Detalle", "Valor"]],
    body: [
      ["Estado", statusLabel],
      ["Creado", formatDate(payin.createdAt)],
    ],
    theme: "grid",
    styles: {
      font: fonts.sans,
      fontSize: 10,
      cellPadding: 8,
      lineColor: RECEIPT_COLORS.line,
      textColor: brandColor,
    },
    headStyles: {
      font: fonts.sans,
      fillColor: RECEIPT_COLORS.surface,
      textColor: RECEIPT_COLORS.brand,
      fontStyle: "bold",
    },
    didParseCell: (hookData) => {
      if (hookData.section === "body" && hookData.row.index === 0) {
        hookData.cell.styles.fontStyle = "bold";
      }
    },
    tableWidth: contentWidth,
    margin: { left: innerX },
  });
  cursorY = ((doc as JsPdfWithAutoTable).lastAutoTable?.finalY || cursorY) + 14;

  // QR movido al resumen (no se repite aquí)

  // Nota final
  doc.setTextColor(mutedColor);
  doc.setFont(fonts.sans, "normal");
  doc.setFontSize(9);
  const footerNote = doc.splitTextToSize(
    "Este comprobante fue generado electrónicamente. Conserva este PDF como respaldo de la operación.",
    contentWidth,
  );
  const footerY = Math.max(cursorY + 18, startY + 60);
  doc.text(footerNote, innerX, footerY);
  cursorY = footerY + 12 * footerNote.length;

  // Marco
  const containerHeight = Math.min(Math.max(cursorY - startY + 32, 320), pageHeight * 0.7);
  doc.setDrawColor(RECEIPT_COLORS.line);
  doc.setLineWidth(1);
  doc.roundedRect(containerX, startY - 30, containerWidth, containerHeight, 12, 12);

  doc.save(`payin-bra-${payin.id}.pdf`);
};
